
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { v4 as uuid } from "uuid";
import storage from "@/services/storage";

import CREATE_DOCUMENT from "@/apollo/documents/CreateDocument.gql";
import CREATE_DOCUMENT_PROJECT from "@/apollo/documents/CreateDocumentProject.gql";

import UploadBoxIcon from "@/components/icons/UploadBoxIcon.vue";
import UIButton from "@/components/button/ui-button.vue";
import { User } from "@/generated/graphql";

import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import splitFileNameExt from "@/utils/splitFileNameExt";
import { FileSizeRestrictions } from "@/entities/FileSizeRestrictions";
import DocumentProjectAccessScope from "@/data/documentAccessScope/DocumentProjectAccessScope";
import UploadedFileData from "@/entities/file/UploadedFileData";
import USER_DATA from "@/apollo/user/GetUserBasicInfo.gql";
import Roles from "@/entities/Roles";

interface HTMLInputEvent extends Event {
    target: HTMLInputElement & EventTarget;
}

@Component({
    components: {
        UploadBoxIcon,
        UIButton
    },
})
export default class UploadDocument extends Vue {
    @Prop({ type: String, required: true }) readonly currentUserId!: string;
    @Prop({ type: String, default: undefined }) readonly buttonLabel!: string;
    @Prop({ type: String, default: undefined })
    readonly attachedToUserId?: string;
    @Prop({ type: String, default: undefined }) readonly companyId?: string;
    @Prop({ type: Object, required: true }) readonly currentUserParent!: User;
    @Prop({ type: String, default: undefined }) readonly projectId?: string;
    @Prop({ type: String, default: undefined }) readonly groupId?: string;
    @Prop({ type: String, default: "transparent" }) readonly variant?: string;
    @Prop({ type: String, default: undefined }) readonly buttonSize?: string;
    @Prop({ type: Boolean, default: false })
    readonly preventAutoUploading!: boolean;
    @Prop({ type: String, default: undefined }) readonly customRequestId?: string;
    @Prop({ type: Boolean, default: false }) readonly showLabel!: boolean;

    processing: boolean = false;
    file: File | null = null;
    userProfile?: User[] = undefined;
    currentUser?: User = undefined;

    created(): void {
        this.getUserProfile();
    }

    get getButtonText(): String {
        return this.buttonLabel ? this.buttonLabel : "Upload";
    }
    get isAdmin(): boolean {
        return this.currentUserParent.role === Roles.CONTRACTOR;
    }

    get additionalData(): Object {
        const projects = {
            data: {
                project_id: this.projectId,
                access_scope: !this.isAdmin ? DocumentProjectAccessScope.ALL : DocumentProjectAccessScope.MANAGEMENT,
            },
        };
        const custom_requests = {
            data: {
                custom_request_id: this.customRequestId,
                created_by: this.currentUserId,
            },
        };
        const data = {
            company_id: this.companyId,
            team_id: this.groupId,
            document_projects: this.projectId ? projects : undefined,
            document_custom_requests: this.customRequestId
                ? custom_requests
                : undefined,
        };

        if (!this.attachedToUserId) {
            return data;
        }

        const attachedTo = {
            attached_to: {
                data: {
                    user_id: this.attachedToUserId,
                },
            },
        };
        return { ...data, ...attachedTo };
    }

    get fileInputElement(): HTMLInputElement {
        return this.$refs.fileInput as HTMLInputElement;
    }

    @Watch("file", { immediate: true })
    onFileChange() {
        this.$emit("fileUpdated", this.file);

        if (!this.file) {
            this.file = null;
            return;
        }

        this.file = this.file;
        const filesize = this.file.size / 1024 / 1024;
        if (filesize >= FileSizeRestrictions.DOCUMENT_MAX) {
            return;
        }

        if (this.file && !this.preventAutoUploading) {
            this.upload();
        }
    }

    addFile(): void {
        this.fileInputElement.click();
    }

    removeFile() {
        this.file = null;
        this.clearFileInputValue();
    }

    clearFileInputValue() {
        this.fileInputElement.value = "";
    }

    async fileChange(event: HTMLInputEvent): Promise<void> {
        const file = event.target.files?.[0];

        if (!file) {
            this.file = null;
            return;
        }

        this.file = file;
        const filesize = file.size / 1024 / 1024;
        if (filesize >= FileSizeRestrictions.DOCUMENT_MAX) {
            this.$eventBus.emit(
                NOTIFICATION,
                Message.errorMessage(
                    `The maximum allowed file size is ${FileSizeRestrictions.DOCUMENT_MAX} MB`
                )
            );
            return;
        }
    }

    async upload(): Promise<UploadedFileData> {
        const fileData = await this.createFile(this.file);
        this.$emit("documentAdded", this.file);
        this.clearFileInputValue();
        return fileData!;
    }

    async createFile(file: any) {
        this.processing = true;
        try {
            const s3UID = await this.uploadFile(file); // we assume uploading is success and we got the ID back please don't push this

            const fileData: any = {
                id: s3UID,
                filename: file.name,
                format: file.type,
                size: file.size.toString(),
                status: "UPLOADED",
                uploaded_by: this.currentUserId,
                added_by:
                    this.currentUser?.given_name + " " + this.currentUser?.family_name,
            };

            if (this.preventAutoUploading) {
                return fileData;
            }
            const res = await this.saveDocumentInfo({
                ...fileData,

                company_id: this.companyId,
                team_id: this.groupId,
            });

            const rres2 = await this.saveProjectDocument({
                document_id: res?.data?.insert_document_one?.id,
                project_id: this.projectId,
                is_core_plan: true,
                access_scope: !this.isAdmin ? DocumentProjectAccessScope.ALL : DocumentProjectAccessScope.MANAGEMENT,
            });
            console.log("🚀 ~ UploadDocument ~ createFile ~ rres2:", rres2)
            console.log("res:::x::::::", res);

            this.$eventBus.emit(
                NOTIFICATION,
                Message.successMessage("File was uploaded")
            );
            this.$emit("refetchDocuments");
        } catch (e) {
            console.log("errrrrroorr");
            console.log(e);
            this.$eventBus.emit(
                NOTIFICATION,
                Message.errorMessage("Something went wrong!")
            );
        } finally {
            this.processing = false;
        }
    }

    async uploadFile(file: any) {
        const uid = uuid();
        const { ext, name } = splitFileNameExt(file.name);
        const s3Filename = `${name}-${uid}.${ext}`;
        await this.saveDocument(s3Filename, file);
        return uid;
    }

    saveDocumentInfo(object: any) {
        return this.$apollo.mutate({
            mutation: CREATE_DOCUMENT,
            variables: { object },
        });


    }


    saveProjectDocument(object: any) {
        return this.$apollo.mutate({
            mutation: CREATE_DOCUMENT_PROJECT,
            variables: { object },
        });


    }
    // async userNameGet(): Promise<void> {
    //   const result:any = this.$apollo.query({
    //     query:  USER_DATA,
    //     variables: {
    //       id: this.currentUserId
    //     },
    //   })

    //   // return result.data.user_by_pk.given_name + result.data.user_by_pk.family_name;
    //   this.userProfile = result;
    // }

    async getUserProfile(): Promise<void> {
        const result: any = await this.$apollo.query({
            query: USER_DATA,
            variables: {
                id: this.currentUserId,
            },
        });
        this.currentUser = result.data.user_by_pk;
    }

    saveDocument(key: string, file: any) {
        return storage
            .save(key, file, () => { })
            .then((result: any) => result.key)
            .catch((err: any) => {
                throw err;
            });
    }
}
